import type { UserEntities } from '@/types/user-profile';

type TwitterUrlEntity = {
  url: string;
  expandedUrl: string;
  displayUrl?: string;
};

type TwitterEntities = {
  description?: { urls?: TwitterUrlEntity[] };
  url?: { urls?: TwitterUrlEntity[] };
};

const LEGACY_EXPANDED_URL_KEY = 'expanded_url';
const LEGACY_DISPLAY_URL_KEY = 'display_url';

type RawTwitterUrlRecord = {
  url?: unknown;
  expandedUrl?: unknown;
  displayUrl?: unknown;
  [LEGACY_EXPANDED_URL_KEY]?: unknown;
  [LEGACY_DISPLAY_URL_KEY]?: unknown;
  [key: string]: unknown;
};

const readUrlString = (candidate: unknown): string | null => (typeof candidate === 'string' ? candidate : null);

const normalizeUrlList = (urls?: RawTwitterUrlRecord[]): TwitterUrlEntity[] | undefined => {
  if (!Array.isArray(urls)) {
    return;
  }
  const normalized = urls
    .map((entry) => {
      const shortUrl = readUrlString(entry?.url);
      if (!shortUrl) {
        return null;
      }
      const expanded = readUrlString(entry?.expandedUrl) ?? readUrlString(entry?.[LEGACY_EXPANDED_URL_KEY]);
      if (!expanded) {
        return null;
      }
      const display = readUrlString(entry?.displayUrl) ?? readUrlString(entry?.[LEGACY_DISPLAY_URL_KEY]) ?? undefined;
      return {
        url: shortUrl,
        expandedUrl: expanded,
        ...(display ? { displayUrl: display } : {}),
      } satisfies TwitterUrlEntity;
    })
    .filter((entry): entry is TwitterUrlEntity => entry !== null);
  return normalized.length > 0 ? normalized : undefined;
};

export function convertUserEntitiesToTwitterEntities(entities?: UserEntities): TwitterEntities | undefined {
  if (!entities) {
    return;
  }

  const descriptionUrls = normalizeUrlList(entities.description?.urls);
  const profileUrls = normalizeUrlList(entities.url?.urls);

  const normalized: TwitterEntities = {};
  if (descriptionUrls) {
    normalized.description = { urls: descriptionUrls };
  }
  if (profileUrls) {
    normalized.url = { urls: profileUrls };
  }
  return Object.keys(normalized).length > 0 ? normalized : undefined;
}
